import { useCallback, useState } from "react";

import { useSelector } from "react-redux";

import { PaymentDaysConfig } from "@evergiving/common/dist/es2015/form";

import { RadioGroup } from "@/lib/components/RadioGroup";
import { LabelText } from "@/lib/components/LabelText";
import { FieldError } from "@/lib/components/FieldError";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { RootState } from "@/lib/redux/types";

export type PaymentDaysFieldProps = {
  config: PaymentDaysConfig;
  onSetPaymentDay: (paymentDay: number) => void;
  value?: number;
};

export const PaymentDaysField = ({
  config,
  onSetPaymentDay,
  value,
}: PaymentDaysFieldProps) => {
  const name = "paymentDay";
  const required = true; // Always required when present
  const errorId = `${name}-errors`;

  const anyErrors = useSelector((state: RootState) => state.errors[name]);
  const fieldValidationErrors = useFieldValidationErrors(config);
  const [isDirty, setIsDirty] = useState(false);

  const { days, labelText } = config;

  const handleChange = useCallback(
    (newValue: number) => {
      onSetPaymentDay(newValue);
      setIsDirty(true);
    },
    [onSetPaymentDay]
  );

  return (
    <>
      <LabelText requiredIndicator>{labelText}</LabelText>
      <RadioGroup<number>
        value={value}
        onChange={handleChange}
        ariaLabel={labelText}
        ariaRequired={required}
        ariaInvalid={(anyErrors && isDirty) || undefined}
        ariaDescribedby={anyErrors ? errorId : undefined}
      >
        {days.map(({ dayOfMonth, label }) => ({
          value: dayOfMonth,
          content: () => <div>{label}</div>,
        }))}
      </RadioGroup>

      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(
              value ? `${value}` : undefined
            )}
          />
        ) : null}
      </div>
    </>
  );
};
